import {ReferenceSelect} from './reference-select.jsx';
import {useColorTheme} from './color-theme.jsx';
import { Children, cloneElement, Fragment, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  PALETTE, NEUTRAL, HEAT_RAMP, DeltaChip,
  parseValueDelta, trendMetricColor, TrendCell, TREND_DOT_COLORS,
} from './shared.jsx';
import { Sparkline } from './charts.jsx';
import { MOTION, useEntranceSuppressed, usePrefersReducedMotion } from './motion.jsx';

// ── Row cascade entrance (motion ronda 14 · v2 2026-07-24) ─────
// Top-to-bottom fade + subtle rise of the tbody rows, FIRST landing
// only. Compositor-only (opacity + translateY(6px)→0), `durBase` per
// row, ~20ms stagger compressed so the LAST row starts no later than
// `CAP - durBase` (total cascade ≤ 600ms regardless of row count).
const ROW_CASCADE_STAGGER_MS = 20;
const ROW_CASCADE_CAP_MS = 600;
/** Force-release for the zero frame. Two painted frames (~33ms) is the
 *  real duration; the rest is margin for a throttled/occluded window.
 *  Mirrors the 450ms backstop of `DataTable` and `useRedrawPulse`. */
const ROW_CASCADE_BACKSTOP_MS = 450;

/**
 * `useRowCascade(entrance, hasRows, rise)` — returns `styleFor(i)` for
 * the tbody `<tr>`s.
 *
 * v2 (2026-07-24, mirrors `dashboard-render/DataTable.tsx` PR #182):
 * the cascade plays on the instance's FIRST non-empty rows landing —
 * whenever that happens. v1 armed only if those rows arrived while the
 * shared `useEntrance()` window (~1.25s after mount) was still open, so
 * any table whose data took longer (filtered fetch, cold cache) mounted
 * DEAD — the "sometimes yes, sometimes no" the user caught. The landing
 * itself now arms a local zero→in flip (render-phase state, FadeSwap's
 * "adjust state during render" pattern).
 *
 * Guards are the system's, unchanged: reduced motion, FadeSwap ghosts
 * (`useEntranceSuppressed`) and hidden documents mount SETTLED (no zero
 * frame at all), and a timeout backstop releases the zero state in
 * occluded windows where Chrome pauses rAF without an event — rows must
 * never stay invisible (regla BI). Pagination, sorting, searching and
 * refetches still NEVER re-animate: `armedRef` latches on the first
 * landing and later row sets mount directly opaque.
 */
function useRowCascade(entrance, hasRows, rise = true) {
  const reduced = usePrefersReducedMotion();
  const suppressed = useEntranceSuppressed();
  const armedRef = useRef(false);
  // null = never animates (settled); 'zero' → 'in' = the cascade.
  const [phase, setPhase] = useState(null);
  if (!armedRef.current && hasRows) {
    armedRef.current = true;
    if (
      Boolean(entrance) &&
      !reduced &&
      !suppressed &&
      typeof requestAnimationFrame === 'function' &&
      !(typeof document !== 'undefined' && document.visibilityState === 'hidden')
    ) {
      setPhase('zero');
    }
  }
  useEffect(() => {
    if (phase !== 'zero') return undefined;
    // Double rAF: the zero state must PAINT before the transition
    // target applies, or the cascade skips. THEN the shared
    // `delayEntrance` beat — these five tables receive `rows` as
    // already-resolved props, so without the hold they would cascade
    // ~250ms BEFORE every chart on the dashboard, breaking the
    // choreography the user calibrated (audit review 2026-07-24).
    // `DataTable` needs no equivalent: its rows arrive from a fetch,
    // so the beat is there naturally.
    let raf2 = null;
    let beat = null;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        beat = setTimeout(() => setPhase('in'), MOTION.delayEntrance);
      });
    });
    const backstop = setTimeout(
      () => setPhase('in'),
      MOTION.delayEntrance + ROW_CASCADE_BACKSTOP_MS,
    );
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2 !== null) cancelAnimationFrame(raf2);
      if (beat !== null) clearTimeout(beat);
      clearTimeout(backstop);
    };
  }, [phase]);
  return (i) => {
    if (phase === null) return undefined;
    if (phase === 'zero') {
      return { opacity: 0, transform: rise ? 'translateY(6px)' : 'none' };
    }
    const delay = Math.min(
      i * ROW_CASCADE_STAGGER_MS,
      ROW_CASCADE_CAP_MS - MOTION.durBase,
    );
    return {
      opacity: 1,
      transform: 'none',
      transition:
        `opacity ${MOTION.durBase}ms ${MOTION.easeOut} ${delay}ms, ` +
        `transform ${MOTION.durBase}ms ${MOTION.easeOut} ${delay}ms`,
    };
  };
}

// ── Table state hook (sort + search + paginate + density) ──────
function useTableState(rows, { searchable = true, pageSize: initialPageSize = 10, initialSort = null } = {}) {
  const [sort, setSort] = useState(initialSort);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [density, setDensity] = useState('comfortable');

  // Reset to first page when filters / page size change
  useEffect(() => { setPage(0); }, [query, pageSize]);

  const filtered = useMemo(() => {
    if (!searchable || !query.trim()) return rows;
    const q = query.trim().toLowerCase();
    return rows.filter(r => Object.values(r).some(v => {
      if (Array.isArray(v) || v == null) return false;
      return String(v).toLowerCase().includes(q);
    }));
  }, [rows, query, searchable]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const { key, dir } = sort;
    const mult = dir === 'asc' ? 1 : -1;
    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = a[key], bv = b[key];
      if (av === bv) return 0;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mult;
      return String(av ?? '').localeCompare(String(bv ?? '')) * mult;
    });
    return copy;
  }, [filtered, sort]);

  const totalFiltered = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = useMemo(
    () => sorted.slice(safePage * pageSize, (safePage + 1) * pageSize),
    [sorted, safePage, pageSize]
  );
  const from = totalFiltered === 0 ? 0 : safePage * pageSize + 1;
  const to = Math.min((safePage + 1) * pageSize, totalFiltered);

  const toggleSort = (key) => setSort(prev => {
    if (!prev || prev.key !== key) return { key, dir: 'asc' };
    if (prev.dir === 'asc') return { key, dir: 'desc' };
    return null;
  });

  return {
    pageRows, totalFiltered, totalAll: rows.length,
    sort, toggleSort,
    query, setQuery,
    page: safePage, setPage, totalPages,
    pageSize, setPageSize,
    density, setDensity,
    from, to,
  };
}

// ── CSV export helper ──────────────────────────────────────────
function toCSV(columns, rows) {
  const esc = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
  const header = columns.map(c => esc(c.label)).join(',');
  const body = rows.map(r =>
    columns.map(c => {
      const v = r[c.key];
      if (Array.isArray(v)) return esc(v.join('|'));
      return esc(v);
    }).join(',')
  ).join('\n');
  return header + '\n' + body;
}
function downloadCSV(filename, columns, rows) {
  const blob = new Blob(['\uFEFF' + toCSV(columns, rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// ── Icons ──────────────────────────────────────────────────────
const SearchIcon = (props) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" {...props}>
    <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/>
  </svg>
);
const DownloadIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);
const ChevronLeft = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>
);
const ChevronRight = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>
);

// ── Toolbar (title + search + density + page size + export) ───
function TableToolbar({
  title, searchable, query, onQueryChange,
  density, onDensityChange, pageSize, onPageSizeChange,
  onExport, totalFiltered, totalAll,
}) {
  const pageSizeId = useId();
  return (
    <div data-table-toolbar="" className="flex flex-wrap items-center gap-3 p-4 border-b border-neutral-200 bg-white">
      {title && <h3 className="font-display text-[15px] font-extrabold leading-6 text-neutral-800 truncate mr-2">{title}</h3>}
      {totalFiltered !== totalAll && (
        <span className="text-[11px] text-neutral-600 whitespace-nowrap">
          {totalFiltered} / {totalAll}
        </span>
      )}
      <div className="flex-1 min-w-[8px]" />
      {searchable && (
        <div className="relative">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none">
            <SearchIcon />
          </span>
          <input
            value={query}
            onChange={e => onQueryChange(e.target.value)}
            placeholder="Buscar..."
            aria-label="Buscar en la tabla"
            className="h-8 w-44 rounded-[10px] border border-control bg-white pl-7 pr-2 text-xs text-neutral-800 placeholder:text-neutral-500 outline-none focus:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 transition-colors"
          />
        </div>
      )}
      <div className="inline-flex rounded-lg border border-neutral-200 bg-neutral-50 p-0.5 gap-0.5" title="Densidad de filas">
        {[
          { k: 'compact',     label: 'Compacta',  rows: [3, 6, 9, 12] },
          { k: 'comfortable', label: 'Cómoda',    rows: [3, 8, 13] },
        ].map(o => (
          <button
            key={o.k}
            onClick={() => onDensityChange(o.k)}
            title={o.label}
            aria-label={o.label}
            aria-pressed={density === o.k}
            className={`h-6 w-7 flex items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 ${
              density === o.k
                ? 'bg-white text-primary-ink shadow-sm ring-1 ring-neutral-200'
                : 'text-neutral-500 hover:text-neutral-800'
            }`}>
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
              {o.rows.map(y => <path key={y} d={`M2 ${y}h12`} />)}
            </svg>
          </button>
        ))}
      </div>
      <label className="sr-only" htmlFor={pageSizeId}>Filas por página</label>
      <ReferenceSelect id={pageSizeId} value={pageSize} aria-label="Filas por página" onChange={e=>onPageSizeChange(Number(e.target.value))} options={Array.from(new Set([pageSize,10,25,50,100])).sort((a,b)=>a-b).map(n=>({value:n,label:`${n} / pág`}))}/>

      {onExport && (
        <button
          onClick={onExport}
          title="Exportar a CSV"
          aria-label="Exportar a CSV"
          className="inline-flex items-center gap-1.5 h-8 rounded-[9px] border border-control bg-white px-2.5 text-xs font-semibold text-neutral-700 hover:bg-primary-soft hover:text-primary-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 transition-colors">
          <DownloadIcon /> CSV
        </button>
      )}
    </div>
  );
}

// ── Pagination (page numbers with ellipsis) ───────────────────
function TablePagination({ page, totalPages, onPageChange, from, to, total }) {
  // Page window computation
  const MAX = 5;
  let start = Math.max(0, page - Math.floor(MAX / 2));
  let end = Math.min(totalPages, start + MAX);
  start = Math.max(0, end - MAX);
  const pages = [];
  for (let i = start; i < end; i++) pages.push(i);

  return (
    <div data-table-pagination="" className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-t border-neutral-100 bg-white text-xs">
      <span className="text-neutral-500">
        {total === 0
          ? <span>Sin resultados</span>
          : <>Mostrando <b className="text-neutral-700 tabular-nums">{from}</b>–<b className="text-neutral-700 tabular-nums">{to}</b> de <b className="text-neutral-700 tabular-nums">{total}</b></>
        }
      </span>
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page === 0}
            aria-label="Página anterior"
            className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-neutral-200 bg-white text-neutral-500 hover:text-accent-dark hover:border-accent/40 disabled:opacity-40 disabled:pointer-events-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30">
            <ChevronLeft />
          </button>
          {start > 0 && (
            <>
              <button
                onClick={() => onPageChange(0)}
                aria-label="Página 1"
                className="h-7 min-w-[28px] px-2 inline-flex items-center justify-center rounded-md border border-neutral-200 bg-white text-xs font-semibold text-neutral-600 hover:text-accent-dark hover:border-accent/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30">
                1
              </button>
              {start > 1 && <span className="text-neutral-500 px-0.5">…</span>}
            </>
          )}
          {pages.map(i => (
            <button
              key={i}
              onClick={() => onPageChange(i)}
              aria-label={`Página ${i + 1}`}
              aria-current={i === page ? 'page' : undefined}
              className={`h-7 min-w-[28px] px-2 inline-flex items-center justify-center rounded-md text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 ${
                i === page
                  ? 'bg-accent text-white shadow-sm'
                  : 'border border-neutral-200 bg-white text-neutral-600 hover:text-accent-dark hover:border-accent/40'
              }`}>
              {i + 1}
            </button>
          ))}
          {end < totalPages && (
            <>
              {end < totalPages - 1 && <span className="text-neutral-500 px-0.5">…</span>}
              <button
                onClick={() => onPageChange(totalPages - 1)}
                aria-label={`Página ${totalPages}`}
                className="h-7 min-w-[28px] px-2 inline-flex items-center justify-center rounded-md border border-neutral-200 bg-white text-xs font-semibold text-neutral-600 hover:text-accent-dark hover:border-accent/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30">
                {totalPages}
              </button>
            </>
          )}
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages - 1}
            aria-label="Página siguiente"
            className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-neutral-200 bg-white text-neutral-500 hover:text-accent-dark hover:border-accent/40 disabled:opacity-40 disabled:pointer-events-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30">
            <ChevronRight />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Empty state row ────────────────────────────────────────────
function TableEmptyRow({ query, colSpan }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-5 py-12">
        <div className="flex flex-col items-center gap-2 text-neutral-500">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <p className="text-xs font-semibold text-neutral-500">
            {query ? <>Sin resultados para <span className="text-neutral-700">"{query}"</span></> : 'Sin datos disponibles'}
          </p>
        </div>
      </td>
    </tr>
  );
}

// ── Sort icon ──────────────────────────────────────────────────
function SortIcon({ active, dir }) {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
      className={`inline-block ml-1 transition-opacity ${active ? 'opacity-100' : 'opacity-30'}`} aria-hidden="true">
      {active
        ? <path d={dir === 'asc' ? 'M7 14l5-5 5 5' : 'M7 10l5 5 5-5'}/>
        : <><path d="M7 10l5-5 5 5"/><path d="M7 14l5 5 5-5"/></>}
    </svg>
  );
}

// Resolve alignment per column, never per cell: nulls and formatted values
// must remain on the same track as the rest of the column.
function tableColumnAlign(col, rows) {
  if (col.align) return col.align;
  return rows.some(row => typeof row[col.key] === 'number' || (Array.isArray(row[col.key]) && row[col.key].some(v => typeof v === 'number'))) ? 'right' : 'left';
}

function Th({ col, sort, onClick, dense }) {
  const active = sort && sort.key === col.key;
  const dir = active ? sort.dir : null;
  const isSortable = col.sortable !== false && onClick;
  const pad = dense ? 'px-3 py-2' : 'px-4 py-[13px]';
  return (
    <th
      scope="col"
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
      style={{textAlign:col.align}}
      onClick={isSortable ? () => onClick(col.key) : undefined}
      className={`${pad} text-left text-2xs font-semibold text-neutral-500 whitespace-nowrap bg-neutral-50 border-b border-neutral-200 ${
        isSortable ? 'cursor-pointer select-none hover:text-neutral-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/40' : ''
      }`}
      tabIndex={isSortable ? 0 : undefined}
      onKeyDown={isSortable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(col.key); } } : undefined}
    >
      {col.label}
      {isSortable && <SortIcon active={active} dir={dir} />}
    </th>
  );
}

// Optional row details share the same fixed action column across table families.
function DetailRows({children, rows, columns, renderRowDetail}) {
  const [active, setActive] = useState(null);
  const id = useId();
  if (!renderRowDetail) return children;
  return Children.map(children, (element, i) => {
    const row = rows[i];
    if (!row) return element;
    const open = active === row;
    return <Fragment key={element.key ?? i}>
      {cloneElement(element, {}, element.props.children, <td data-row-action-column="" className="sticky right-0 z-[3] w-32 min-w-32 border-l border-neutral-200 bg-white [tr:hover>&]:bg-primary-soft/40 px-3 py-1.5 text-center align-top max-sm:w-[52px] max-sm:min-w-[52px] max-sm:px-1">
        <button type="button" data-row-detail-action="" aria-label={`${open ? 'Cerrar detalle' : 'Ver detalle'}: ${row[columns[0]?.key] ?? i + 1}`} aria-expanded={open} aria-controls={`${id}-${i}`} onClick={e=>{e.stopPropagation();setActive(open ? null : row);}} className="inline-flex min-h-7 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-control bg-white px-2 py-[5px] text-2xs font-semibold leading-4 text-primary transition-colors hover:border-primary hover:bg-primary-soft [tr:hover_&]:border-primary [tr:hover_&]:bg-primary-soft aria-expanded:bg-primary-soft aria-expanded:text-neutral-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary max-sm:min-h-10 max-sm:min-w-10 max-sm:p-2">
          <span aria-hidden="true">{open ? '⌃' : '⌄'}</span><span data-detail-label="" className="max-sm:sr-only">{open ? 'Cerrar detalle' : 'Ver detalle'}</span>
        </button>
      </td>)}
      {open && <tr data-row-expansion=""><td colSpan={columns.length+1} className="p-0"><div id={`${id}-${i}`} className="border-y border-neutral-200 bg-primary-soft px-5 py-4 text-2xs text-neutral-700">{renderRowDetail(row)}</div></td></tr>}
    </Fragment>;
  });
}
function DetailHeader() { return <th scope="col" data-row-action-column="" className="sticky right-0 z-[4] w-32 min-w-32 border-l border-b border-neutral-200 bg-neutral-50 px-3 py-[13px] text-center text-2xs font-semibold text-neutral-500 max-sm:w-[52px] max-sm:min-w-[52px] max-sm:px-1"><span data-detail-label="" className="max-sm:sr-only">Ver detalle</span><span className="hidden max-sm:inline" aria-hidden="true">···</span></th>; }

// ── Shared wrapper ─────────────────────────────────────────────
// Internal helper used by every table variant below.
function TableShell({
  title, columns, state, rows, zebra, maxHeight, sortable,
  searchable, onExport, children, renderRowDetail,
}) {
  const dense = state.density === 'compact';
  return (
    <div data-table-surface="" data-density={state.density} className="rounded-[12px] border border-neutral-200 bg-white shadow-card overflow-hidden">
      <TableToolbar
        title={title}
        searchable={searchable}
        query={state.query}
        onQueryChange={state.setQuery}
        density={state.density}
        onDensityChange={state.setDensity}
        pageSize={state.pageSize}
        onPageSizeChange={state.setPageSize}
        totalFiltered={state.totalFiltered}
        totalAll={rows.length}
        onExport={onExport ? () => downloadCSV(`${(title || 'tabla').toLowerCase().replace(/\s+/g, '-')}.csv`, columns, rows) : null}
      />
      <div className="overflow-auto sticky-thead" style={maxHeight ? { maxHeight } : undefined}>
        <table className="w-full text-xs">
          <thead>
            <tr>{columns.map(c => <Th key={c.key} col={{...c,align:tableColumnAlign(c,rows)}} sort={state.sort} onClick={sortable ? state.toggleSort : null} dense={dense} />)}{renderRowDetail && <DetailHeader/>}</tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {state.pageRows.length === 0
              ? <TableEmptyRow query={state.query} colSpan={columns.length + (renderRowDetail ? 1 : 0)} />
              : <DetailRows rows={state.pageRows} columns={columns} renderRowDetail={renderRowDetail}>{children(state.pageRows, dense, zebra)}</DetailRows>}
          </tbody>
        </table>
      </div>
      <TablePagination
        page={state.page} totalPages={state.totalPages} onPageChange={state.setPage}
        from={state.from} to={state.to} total={state.totalFiltered}
      />
    </div>
  );
}

// ── Heatmap Table ─────────────────────────────────────────────
// `deltaColumns` (optional): numeric columns rendered as semantic
// ▲/▼ pastel delta chips (rule 4) — same contract as the other
// table variants, so a heat table can carry a Δ column too.
export function HeatmapTable({
  columns, rows, heatColumns = [], deltaColumns = [], title,
  sortable = true, searchable = true, pageSize = 10,
  zebra = false, maxHeight, onExport = true, entrance = true, renderRowDetail, colorScale, legend, domain, formatValue, missingLabel = '—',
}) {
  columns = columns.map(col => heatColumns.includes(col.key) ? {...col, align:col.align || 'center'} : col);
  const clientTheme = useColorTheme('HeatmapTable');
  if (clientTheme.override) colorScale = clientTheme.heatScale;
  colorScale = colorScale?.length ? colorScale : clientTheme.heatScale;
  const state = useTableState(rows, { searchable, pageSize });
  // Fade-only cascade (rise=false): the tinted cells fade in while the
  // values stay put — a heat surface that slides reads as data motion.
  const rowStyle = useRowCascade(entrance, rows.length > 0, false);

  const ranges = useMemo(() => {
    const r = {};
    heatColumns.forEach(col => {
      const vals = rows.map(row => row[col]).filter(v => typeof v === 'number' && Number.isFinite(v));
      if (!vals.length) return;
      r[col] = domain ? {min:domain[0],max:domain[1]} : { min: Math.min(...vals), max: Math.max(...vals) };
    });
    return r;
  }, [rows, heatColumns, domain]);

  // F3b: heat cells ramp on the shared HEAT_RAMP (magenta, accent
  // 50→900) instead of the old accent-* (morado) Tailwind ladder.
  // Inline styles so the colors don't depend on the consumer's
  // Tailwind content scan.
  const heatStyle = (col, val) => {
    if (!ranges[col]) return undefined;
    const { min, max } = ranges[col];
    const t = max === min ? 0.5 : (val - min) / (max - min);
    const colors = colorScale.length ? colorScale : HEAT_RAMP;
    const idx = Math.min(colors.length - 1, Math.max(0, Math.floor(t * colors.length)));
    const color = colors[idx];
    // Choose a readable foreground from the actual color, not its ramp index.
    const hex = /^#[0-9a-f]{6}$/i.test(color) ? color.slice(1) : 'FFFFFF';
    const rgb = [0,2,4].map(i=>parseInt(hex.slice(i,i+2),16)/255).map(v=>v<=.04045?v/12.92:((v+.055)/1.055)**2.4);
    const luminance = .2126*rgb[0]+.7152*rgb[1]+.0722*rgb[2];
    return { background: color, color: luminance < .179 ? 'white' : NEUTRAL.text };
  };

  return (
    <div data-heat-table=""><TableShell title={title} columns={columns} rows={rows} state={state}
      zebra={zebra} maxHeight={maxHeight} sortable={sortable}
      searchable={searchable} onExport={onExport} renderRowDetail={renderRowDetail}>
      {(pageRows, dense) => {
        const pad = dense ? 'px-3 py-1.5' : 'px-4 py-[13px]';
        return pageRows.map((row, idx) => (
          <tr key={idx} style={rowStyle(idx)} className={`transition-colors hover:bg-primary-soft/40 [&>td:first-child]:font-semibold [&>td:first-child]:text-neutral-800 ${zebra && idx % 2 ? 'bg-neutral-50' : 'bg-white'}`}>
            {columns.map(c => {
              const val = row[c.key];
              const isHeat = heatColumns.includes(c.key) && typeof val === 'number' && Number.isFinite(val);
              if (!isHeat && deltaColumns.includes(c.key) && typeof val === 'number') {
                // Rule 4: semantic ▲/▼ pastel delta chip.
                return (
                  <td key={c.key} data-numeric={typeof val==='number'||undefined} style={{textAlign:tableColumnAlign(c,rows)}} className={`${pad} tabular-nums`}>
                    <DeltaChip delta={val} sentiment={c.deltaSentiment} label={c.format ? c.format(val) : `${val > 0 ? '+' : ''}${val}${c.deltaUnit??'%'}`} className="text-2xs" />
                  </td>
                );
              }
              return (
                <td
                  key={c.key}
                  data-numeric={typeof val==='number'||undefined} style={{textAlign:tableColumnAlign(c,rows)}}
                  className={`${isHeat ? 'px-[5px] py-1.5' : pad} tabular-nums ${isHeat ? 'text-center' : 'text-neutral-700'}`}>
                  {isHeat ? <span data-heat-cell="" className="block min-w-16 rounded-[5px] px-2.5 py-[9px] text-center text-xs font-semibold" style={heatStyle(c.key, val)}>{formatValue ? formatValue(val,c.key) : val.toLocaleString()}</span>
                    : val == null || (typeof val === 'number' && !Number.isFinite(val)) ? <span data-no-data="">—</span>
                    : typeof val === 'number' ? val.toLocaleString() : val}
                </td>
              );
            })}
          </tr>
        ));
      }}
    </TableShell>{legend && <div data-heat-legend="" className="flex flex-wrap items-center justify-end gap-2.5 px-0.5 pt-[13px] text-2xs text-neutral-500"><span>{legend}</span><span className="flex gap-[3px]">{(colorScale.length ? colorScale : HEAT_RAMP).map(c=><i key={c} className="block h-[9px] w-[19px] rounded-sm" style={{background:c}}/>)}</span><span>{missingLabel}</span></div>}</div>
  );
}

// ── Sparkline Table ───────────────────────────────────────────
// Redesign v2: every row's sparkline renders in the brand magenta
// (PALETTE[0]) — the old per-row palette cycling was color-as-
// decoration (row order carried no meaning). The sparkline cell also
// shows the bold last value of the series ("somos BI", rule 1).
// `deltaColumns` (optional, same contract as StatusTable /
// ComparisonTable): numeric columns rendered as semantic ▲/▼ pastel
// delta chips (rule 4).
export function SparklineTable({
  columns, rows, sparkColumn, deltaColumns = [], title,
  sortable = true, searchable = true, pageSize = 10,
  zebra = false, maxHeight, onExport = true, entrance = true, renderRowDetail, color = PALETTE[0],
}) {
  const cols = useMemo(
    () => columns.map(c => c.key === sparkColumn ? { ...c, sortable: false } : c),
    [columns, sparkColumn]
  );
  const state = useTableState(rows, { searchable, pageSize });
  // Row cascade; the per-row Sparkline draws itself in via its own
  // `entrance` (useLineDraw) — forwarded so the opt-out covers both.
  const rowStyle = useRowCascade(entrance, rows.length > 0);

  return (
    <TableShell title={title} columns={cols} rows={rows} state={state}
      zebra={zebra} maxHeight={maxHeight} sortable={sortable}
      searchable={searchable} onExport={onExport} renderRowDetail={renderRowDetail}>
      {(pageRows, dense) => {
        const pad = dense ? 'px-3 py-1.5' : 'px-4 py-[13px]';
        return pageRows.map((row, idx) => (
          <tr key={idx} style={rowStyle(idx)} className={`transition-colors hover:bg-primary-soft/40 [&>td:first-child]:font-semibold [&>td:first-child]:text-neutral-800 ${zebra && idx % 2 ? 'bg-neutral-50' : 'bg-white'}`}>
            {cols.map(c => {
              const val = row[c.key];
              if (c.key === sparkColumn && Array.isArray(val)) {
                const last = val[val.length - 1];
                return (
                  <td key={c.key} data-numeric={typeof val==='number'||undefined} style={{textAlign:tableColumnAlign(c,rows)}} className={`${pad} tabular-nums`}>
                    <span className="inline-flex items-center gap-2 align-middle">
                      <Sparkline data={val} color={color} width={80} height={24} entrance={entrance} />
                      {typeof last === 'number' && Number.isFinite(last) && (
                        <span className="text-xs font-bold text-neutral-800">{last.toLocaleString()}</span>
                      )}
                    </span>
                  </td>
                );
              }
              if (deltaColumns.includes(c.key) && typeof val === 'number') {
                // Rule 4: semantic ▲/▼ pastel delta chip.
                return (
                  <td key={c.key} data-numeric={typeof val==='number'||undefined} style={{textAlign:tableColumnAlign(c,rows)}} className={`${pad} tabular-nums`}>
                    <DeltaChip delta={val} sentiment={c.deltaSentiment} label={c.format ? c.format(val) : `${val > 0 ? '+' : ''}${val}${c.deltaUnit??'%'}`} className="text-2xs" />
                  </td>
                );
              }
              return (
                <td key={c.key} data-numeric={typeof val==='number'||undefined} style={{textAlign:tableColumnAlign(c,rows)}} className={`${pad} text-neutral-700 tabular-nums`}>
                  {val==null?<span data-no-data="">—</span>:c.format?c.format(val):typeof val === 'number' ? val.toLocaleString() : val}
                </td>
              );
            })}
          </tr>
        ));
      }}
    </TableShell>
  );
}

// ── Comparison Table ──────────────────────────────────────────
export function ComparisonTable({
  columns, rows, compareKeys, title,
  sortable = true, searchable = true, pageSize = 10,
  zebra = false, maxHeight, onExport = true, entrance = true, renderRowDetail,
}) {
  const state = useTableState(rows, { searchable, pageSize });
  const rowStyle = useRowCascade(entrance, rows.length > 0);

  return (
    <TableShell title={title} columns={columns} rows={rows} state={state}
      zebra={zebra} maxHeight={maxHeight} sortable={sortable}
      searchable={searchable} onExport={onExport} renderRowDetail={renderRowDetail}>
      {(pageRows, dense) => {
        const pad = dense ? 'px-3 py-1.5' : 'px-4 py-[13px]';
        return pageRows.map((row, idx) => (
          <tr key={idx} style={rowStyle(idx)} className={`transition-colors hover:bg-primary-soft/40 [&>td:first-child]:font-semibold [&>td:first-child]:text-neutral-800 ${zebra && idx % 2 ? 'bg-neutral-50' : 'bg-white'}`}>
            {columns.map(c => {
              const val = row[c.key];
              if (compareKeys?.includes(c.key) && typeof val === 'number') {
                // Rule 4: semantic ▲/▼ pastel delta chip.
                return (
                  <td key={c.key} data-numeric={typeof val==='number'||undefined} style={{textAlign:tableColumnAlign(c,rows)}} className={`${pad} tabular-nums`}>
                    <DeltaChip delta={val} sentiment={c.deltaSentiment} label={c.format ? c.format(val) : `${val > 0 ? '+' : ''}${val}${c.deltaUnit??'%'}`} className="text-2xs" />
                  </td>
                );
              }
              return (
                <td key={c.key} data-numeric={typeof val==='number'||undefined} style={{textAlign:tableColumnAlign(c,rows)}} className={`${pad} text-neutral-700 tabular-nums`}>
                  {val==null?<span data-no-data="">—</span>:c.format?c.format(val):typeof val === 'number' ? val.toLocaleString() : val}
                </td>
              );
            })}
          </tr>
        ));
      }}
    </TableShell>
  );
}

// ── Trend Matrix Table ────────────────────────────────────────
// The "Comparativa por trimestre fiscal" table from the approved
// redesign mockup (design_tmp/redesign_widgets/1_executive.html):
// each period cell shows the bold value, a micro-bar whose width is
// the magnitude relative to that METRIC's (row's) max across periods,
// and a semantic ▲/▼ pastel delta chip vs the previous period.
//
// Cell values are the SQL-formatted strings the product actually
// emits ("164,149 ▼ -9.6%", "28 —", "96.2% ▲ +0.5 pp", bare
// "181,596") parsed with the STRICT `parseValueDelta` — a cell that
// doesn't match the grammar renders its raw text unchanged (style
// degrades, data never does). The metric (first) column may prefix a
// colored emoji dot (🔴🟢🟣🟠); it maps deterministically to the
// row's bar color via `trendMetricColor` and renders as a styled dot.
export function TrendMatrixTable({
  columns = [],
  rows = [],
  metricKey,
  title,
  subtitle,
  entrance = true, renderRowDetail,
}) {
  const rowStyle = useRowCascade(entrance, rows.length > 0);
  const mKey = metricKey || columns[0]?.key;
  const trendKeys = columns.filter(c => c.key !== mKey).map(c => c.key);

  return (
    <div data-table-surface="" className="rounded-[12px] border border-neutral-200 bg-white shadow-card overflow-hidden">
      {(title || subtitle) && (
        <div className="px-4 pt-3 pb-1.5">
          {title && <h3 className="font-display text-[15px] font-extrabold leading-6 text-neutral-800 truncate">{title}</h3>}
          {subtitle && <p className="mt-0.5 text-2xs text-neutral-500">{subtitle}</p>}
        </div>
      )}
      <div className="overflow-x-auto px-2 pb-2">
        <table className="w-full text-xs">
          <thead>
            <tr>
              {columns.map((c, i) => (
                <th
                  key={c.key}
                  scope="col"
                  style={{textAlign:c.align || (c.key === mKey ? "left" : "right")}}
                  className={`px-3 py-[13px] text-2xs font-semibold text-neutral-500 whitespace-nowrap bg-neutral-50 border-b border-neutral-200 ${
                    c.key === mKey || i === 0 ? 'text-left' : 'text-right'
                  }`}>
                  {c.label}
                </th>
              ))}
              {renderRowDetail && <DetailHeader/>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns.length + (renderRowDetail ? 1 : 0)} className="px-4 py-8 text-center text-xs text-neutral-500">
                  Sin datos disponibles
                </td>
              </tr>
            )}
            <DetailRows rows={rows} columns={columns} renderRowDetail={renderRowDetail}>{rows.map((row, idx) => {
              const rawLabel = String(row[mKey] ?? '').trim();
              const dot = Object.keys(TREND_DOT_COLORS).find(d => rawLabel.startsWith(d)) || null;
              const metricName = dot ? rawLabel.slice(dot.length).trim() : rawLabel;
              const barColor = trendMetricColor(rawLabel);
              // Row max over the |values| of the parseable trend cells —
              // the micro-bar scale is per METRIC (row), like the mockup.
              const parsedByKey = {};
              let max = 0;
              for (const k of trendKeys) {
                const p = parseValueDelta(row[k]);
                parsedByKey[k] = p;
                if (p && Number.isFinite(p.value)) max = Math.max(max, Math.abs(p.value));
              }
              return (
                <tr key={idx} style={rowStyle(idx)} className="border-t border-neutral-100 bg-white transition-colors hover:bg-primary-soft/40">
                  {columns.map((c) => {
                    if (c.key === mKey) {
                      return (
                        <td key={c.key} data-numeric={c.key !== mKey || undefined} style={{textAlign:c.align || (c.key === mKey ? 'left' : 'right')}} className="px-3 py-[13px] font-semibold text-neutral-800 whitespace-nowrap align-top">
                          {dot && (
                            <span
                              aria-hidden="true"
                              className="mr-1.5 inline-block h-2 w-2 rounded-full align-middle"
                              style={{ background: TREND_DOT_COLORS[dot] }}
                            />
                          )}
                          {metricName}
                        </td>
                      );
                    }
                    const parsed = parsedByKey[c.key];
                    if (!parsed) {
                      // Strict no-parse → raw text exactly as-is.
                      return (
                        <td key={c.key} data-numeric={c.key !== mKey || undefined} style={{textAlign:c.align || (c.key === mKey ? 'left' : 'right')}} className="px-3 py-[13px] text-right text-neutral-700 tabular-nums align-top">
                          {row[c.key]}
                        </td>
                      );
                    }
                    return (
                      <td key={c.key} data-numeric={c.key !== mKey || undefined} style={{textAlign:c.align || (c.key === mKey ? 'left' : 'right')}} className="px-3 py-[13px] text-right align-top">
                        <TrendCell parsed={parsed} max={max} color={barColor} />
                      </td>
                    );
                  })}
                </tr>
              );
            })}</DetailRows>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Status Table ──────────────────────────────────────────────
// `deltaColumns` (optional, F4b): numeric columns listed here render
// as semantic ▲/▼ pastel delta chips (rule 4) — signed percent, same
// contract as ComparisonTable's `compareKeys`.
export function StatusTable({
  columns, rows, statusColumn, progressColumn, deltaColumns = [], title,
  sortable = true, searchable = true, pageSize = 10,
  zebra = false, maxHeight, onExport = true, entrance = true, renderRowDetail, progressColor = PALETTE[0],
}) {
  const state = useTableState(rows, { searchable, pageSize });
  const rowStyle = useRowCascade(entrance, rows.length > 0);
  const statusColors = {
    active:   'bg-success-soft text-success-dark border-success/30',
    warning:  'bg-warning-soft text-warning-dark border-warning/30',
    critical: 'bg-error-soft text-error-dark border-error/30',
    inactive: 'bg-neutral-100 text-neutral-600 border-neutral-200',
    pending:  'bg-primary-soft text-primary-ink border-primary-line',
  };
  return (
    <TableShell title={title} columns={columns} rows={rows} state={state}
      zebra={zebra} maxHeight={maxHeight} sortable={sortable}
      searchable={searchable} onExport={onExport} renderRowDetail={renderRowDetail}>
      {(pageRows, dense) => {
        const pad = dense ? 'px-3 py-1.5' : 'px-4 py-[13px]';
        return pageRows.map((row, idx) => (
          <tr key={idx} style={rowStyle(idx)} className={`transition-colors hover:bg-primary-soft/40 [&>td:first-child]:font-semibold [&>td:first-child]:text-neutral-800 ${zebra && idx % 2 ? 'bg-neutral-50' : 'bg-white'}`}>
            {columns.map(c => {
              const val = row[c.key];
              if (c.key === statusColumn) {
                const cls = statusColors[val?.toLowerCase()] || statusColors.inactive;
                return (
                  <td key={c.key} data-numeric={typeof val==='number'||undefined} style={{textAlign:tableColumnAlign(c,rows)}} className={pad}>
                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${cls}`}>
                      <span className="h-1.5 w-1.5 rounded-full bg-current"/>{c.format ? c.format(val) : val}
                    </span>
                  </td>
                );
              }
              if (c.key === progressColumn && typeof val === 'number') {
                // Redesign v2: progress bars render in the brand magenta
                // with the bold value always visible — completion is not
                // a good/bad signal, so no traffic-light coloring.
                return (
                  <td key={c.key} data-numeric={typeof val==='number'||undefined} style={{textAlign:tableColumnAlign(c,rows)}} className={pad}>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 rounded-full bg-neutral-100 overflow-hidden" style={{ minWidth: 80 }}>
                        <div className="h-full rounded-full" style={{ width: `${Math.max(0,Math.min(100,val))}%`, background: progressColor }}/>
                      </div>
                      <span className="text-xs font-bold tabular-nums text-neutral-800 w-9 text-right">{val}%</span>
                    </div>
                  </td>
                );
              }
              if (deltaColumns.includes(c.key) && typeof val === 'number') {
                // Rule 4: semantic ▲/▼ pastel delta chip.
                return (
                  <td key={c.key} data-numeric={typeof val==='number'||undefined} style={{textAlign:tableColumnAlign(c,rows)}} className={`${pad} tabular-nums`}>
                    <DeltaChip delta={val} sentiment={c.deltaSentiment} label={c.format ? c.format(val) : `${val > 0 ? '+' : ''}${val}${c.deltaUnit??'%'}`} className="text-2xs" />
                  </td>
                );
              }
              return (
                <td key={c.key} data-numeric={typeof val==='number'||undefined} style={{textAlign:tableColumnAlign(c,rows)}} className={`${pad} text-neutral-700 tabular-nums`}>
                  {val==null?<span data-no-data="">—</span>:c.format?c.format(val):typeof val === 'number' ? val.toLocaleString() : val}
                </td>
              );
            })}
          </tr>
        ));
      }}
    </TableShell>
  );
}
